'use client';

import { QueriesBoundary } from '@shared/api-utils';
import { ROUTES } from '@shared/utils';
import { useTranslations } from 'next-intl';
import type { ComponentProps } from 'react';
import type { LotteryName } from '../../../lottery-purchases/types';
import { ResultsDrawHistoryHeader } from '../../components';
import { useDrawHistory } from '../../hooks/useDrawHistory/useDrawHistory';
import { ResultsPageSkeleton } from '../../skeletons/ResultsPageSkeleton';
import type { ResultsConfig } from '../../types';
import { ResultsPageLayout } from '../ResultsPageLayout/ResultsPageLayout';

type ResultsDrawHistoryPageProps = ComponentProps<'div'> & {
  lotteryName: LotteryName;
  config?: ResultsConfig;
  backFallbackUrl?: string;
  headerTitle?: string;
};

/**
 * Draw-history archive page: cascading date filters by lottery cadence, prize tables for the selected draw.
 * Page-level query failures rethrow so Next.js `error.tsx` can render the shared error page.
 */
function ResultsDrawHistoryPage({ lotteryName, config, backFallbackUrl, headerTitle, children, ...props }: ResultsDrawHistoryPageProps) {
  const t = useTranslations('lotteries.results');
  const { currentResult, dropdowns, datesQuery, resultsQuery } = useDrawHistory(lotteryName);

  return (
    <QueriesBoundary
      queries={[datesQuery, resultsQuery]}
      loadingFallback={<ResultsPageSkeleton />}
      errorFallbackRender={({ error }) => {
        throw error;
      }}
    >
      {() => {
        if (!currentResult) {
          return (
            <div className="flex items-center justify-center min-h-96" data-testid="draw-history-empty">
              <p className="text-muted-foreground">{t('archive_empty_state')}</p>
            </div>
          );
        }

        const betUrl = ROUTES.LOTTO.LOTTERIES[lotteryName.toUpperCase() as keyof typeof ROUTES.LOTTO.LOTTERIES];

        return (
          <ResultsPageLayout
            {...props}
            lotteryName={lotteryName}
            config={config}
            currentResult={currentResult}
            betUrl={betUrl}
            backFallbackUrl={backFallbackUrl}
            headerTitle={headerTitle}
            header={<ResultsDrawHistoryHeader lotteryName={lotteryName} dropdowns={dropdowns} currentResult={currentResult} />}
          >
            {children}
          </ResultsPageLayout>
        );
      }}
    </QueriesBoundary>
  );
}

export { ResultsDrawHistoryPage };
export type { ResultsDrawHistoryPageProps };
